import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Lock, Undo2, Eye, Send } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../components/ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../../components/ui/dialog';
import { Textarea } from '../../../components/ui/textarea';
import ReportWorkflowTrail from '../../reports/ReportWorkflowTrail';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAtcSummary = (record, prefix) => {
    const codes = record?.[`${prefix}_atc_codes`];
    if (Array.isArray(codes) && codes.filter(Boolean).length) {
        return codes.filter(Boolean).join(', ');
    }
    return [1, 2, 3, 4]
        .map(i => record?.[`${prefix}_atc_${i}`])
        .filter(Boolean)
        .join(', ');
};

const getCustomerOptionValue = (customer) => (
    customer?.customer_id || customer?.id || customer?.customer_code || customer?.customer_name || ''
);

const findCustomerByLookup = (customers, lookup) => {
    const normalized = String(lookup || '').trim().toLowerCase();
    return customers.find((customer) => [
        customer?.customer_id,
        customer?.id,
        customer?.customer_code,
        customer?.customer_name
    ].some(value => String(value || '').trim().toLowerCase() === normalized));
};

function ARInvoicesTab({ customers, fmt, onRefresh, selectedCourse, permissions, viewingStudentId, viewingStudentName, isReadOnly = false, sourceOpenRequest = null }) {
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [showReversalDialog, setShowReversalDialog] = useState(false);
    const [reversalTarget, setReversalTarget] = useState(null);
    const [reversalReason, setReversalReason] = useState('');
    const [reversing, setReversing] = useState(false);
    const [lastResult, setLastResult] = useState(null);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const lastSourceOpenRequestId = useRef(null);
    const [form, setForm] = useState({
        customer_id: '',
        invoice_date: new Date().toISOString().split('T')[0],
        due_date: '',
        particulars: '',
        total_amount: 0,
        tax_amount: 0
    });
    
    const basePerms = permissions || { can_create: true, can_post: false, can_cancel: false, can_unpost: false };
    const perms = isReadOnly
        ? { ...basePerms, can_create: false, can_post: false, can_cancel: false, can_unpost: false }
        : basePerms;
    const canPostInCurrentScope = !isReadOnly && perms.can_post;
    const canReverseInCurrentScope = basePerms.can_unpost && (!isReadOnly || !!viewingStudentId);
    const draftReviewState = canPostInCurrentScope
        ? 'You can review and post it after checking customer and sales support.'
        : 'Submit it for instructor/admin review when ready. Submitted invoices appear in Review > AR/AP Review Queue.';
    const draftNextAction = canPostInCurrentScope
        ? 'Open the invoice row to review customer, date, amount, and support before posting.'
        : 'Click Submit for Review so the instructor can approve/post it or return it with feedback.';
    
    const fetchInvoices = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const params = new URLSearchParams();
            if (viewingStudentId) params.append('student_id', viewingStudentId);
            const response = await axios.get(`${API}/ar/invoices${params.toString() ? `?${params.toString()}` : ''}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setInvoices(response.data);
            setLoading(false);
        } catch (error) {
            toast.error('Failed to load invoices');
            setLoading(false);
        }
    }, [viewingStudentId]);
    
    useEffect(() => {
        fetchInvoices();
    }, [fetchInvoices]);

    useEffect(() => {
        if (!sourceOpenRequest?.id || !sourceOpenRequest.sourceType?.startsWith('ar_invoice') || loading) return;
        if (lastSourceOpenRequestId.current === sourceOpenRequest.id) return;

        const requestedId = String(sourceOpenRequest.sourceId || '').trim();
        const requestedRef = String(sourceOpenRequest.refNo || '').trim().toLowerCase();
        const matchingInvoice = invoices.find((invoice) => {
            const invoiceId = String(invoice.id || '').trim();
            const invoiceNumber = String(invoice.invoice_number || '').trim();
            const invoiceNumberLower = invoiceNumber.toLowerCase();
            return (requestedId && invoiceId === requestedId)
                || (requestedId && invoiceNumber === requestedId)
                || (requestedRef && invoiceNumberLower === requestedRef)
                || (requestedRef && requestedRef === `rev-${invoiceNumberLower}`);
        });

        if (!matchingInvoice && invoices.length === 0) return;

        lastSourceOpenRequestId.current = sourceOpenRequest.id;
        if (matchingInvoice) {
            setSelectedInvoice(matchingInvoice);
            toast.success(`Opened ${sourceOpenRequest.label || 'AR Invoice'} ${matchingInvoice.invoice_number} from ${sourceOpenRequest.sourceOrigin || 'the source trail'}.`);
        } else {
            toast.info(`The AR Invoice source ${sourceOpenRequest.refNo || sourceOpenRequest.sourceId || ''} is not visible in this current course/student scope.`);
        }
    }, [sourceOpenRequest, invoices, loading]);
    
    const handleCreate = async () => {
        if (!form.customer_id || !form.particulars || !form.total_amount) {
            toast.error('Please fill all required fields');
            return;
        }
        const amount = parseFloat(form.total_amount);
        if (isNaN(amount) || amount <= 0) {
            toast.error('Subtotal must be a valid number greater than 0');
            return;
        }
        const taxAmount = parseFloat(form.tax_amount) || 0;
        if (taxAmount < 0) {
            toast.error('VAT/Tax must be zero or greater');
            return;
        }
        const invoiceTotal = amount + taxAmount;
        
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(`${API}/ar/invoices`, {
                ...form,
                course_id: selectedCourse || null,
                total_amount: amount,
                tax_amount: taxAmount,
                items: [{ description: form.particulars, quantity: 1, unit_price: amount, amount: amount }]
            }, { headers: { Authorization: `Bearer ${token}` } });
            const customer = findCustomerByLookup(customers, form.customer_id);
            const invoiceNumber = response.data?.invoice_number || 'New invoice';
            setLastResult({
                invoiceNumber,
                status: 'Draft',
                customerName: customer?.customer_name || form.customer_id,
                date: form.invoice_date,
                amount: invoiceTotal,
                reference: form.particulars,
                location: 'Record > AR Invoice list. Submit it next to send it to Review > AR/AP Review Queue.',
                reviewState: draftReviewState,
                glImpact: 'Draft only. It does not affect GL, Trial Balance, AR balances, or reports until posted.',
                nextSafeAction: draftNextAction
            });
            toast.success(
                `AR Invoice ${response.data?.invoice_number || ''} saved as Draft. Find it in Record > AR Invoice; it is not in GL/Trial Balance yet. Next: click Submit for Review so the instructor can approve/post or return it.`
            );
            setShowForm(false);
            setForm({ customer_id: '', invoice_date: new Date().toISOString().split('T')[0], due_date: '', particulars: '', total_amount: 0, tax_amount: 0 });
            fetchInvoices();
            if (onRefresh) onRefresh();
        } catch (error) {
            const detail = error.response?.data?.detail;
            const msg = typeof detail === 'string' ? detail
                : Array.isArray(detail) ? detail.map(d => d.msg || d.message || JSON.stringify(d)).join('; ')
                : error.response?.data?.message || 'Failed to create invoice';
            toast.error(msg);
        }
    };

    const handleSubmitForReview = async (inv) => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.put(`${API}/ar/invoices/${inv.id}/submit`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const updated = response.data?.invoice || inv;
            setLastResult({
                invoiceNumber: updated.invoice_number || inv.invoice_number,
                status: 'Submitted for Review',
                customerName: updated.customer_name || inv.customer_name,
                date: updated.invoice_date || inv.invoice_date,
                amount: updated.net_amount || updated.total_amount || inv.net_amount || inv.total_amount,
                reference: updated.particulars || inv.particulars || 'AR invoice review',
                location: response.data?.next_location || 'Review > AR/AP Review Queue',
                reviewState: 'Instructor/admin reviews next and can approve/post or return with feedback.',
                glImpact: response.data?.gl_impact || 'Submitted only. No GL or Trial Balance impact until posted.',
                nextSafeAction: 'Wait for instructor review, or monitor this invoice status in Record > AR Invoice.'
            });
            toast.success(`AR Invoice ${updated.invoice_number || inv.invoice_number} submitted for instructor/admin review. Find it now in Review > AR/AP Review Queue and in your AR Invoice list as Submitted for Review; GL/Trial Balance changes only after posting.`);
            fetchInvoices();
            if (onRefresh) onRefresh();
        } catch (error) {
            const detail = error.response?.data?.detail;
            toast.error(typeof detail === 'string' ? detail : 'Failed to submit invoice for review');
        }
    };
    
    const handlePost = async (inv) => {
        try {
            const token = localStorage.getItem('token');
            await axios.put(`${API}/ar/invoices/${inv.id}/post`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setLastResult({
                invoiceNumber: inv.invoice_number,
                status: 'Posted',
                customerName: inv.customer_name,
                date: inv.invoice_date,
                amount: inv.net_amount || inv.total_amount,
                reference: inv.particulars || inv.reference_number || 'AR invoice posting',
                location: 'Record > AR Invoice, General Ledger, Trial Balance, AR Summary, and AR Aging',
                reviewState: 'Instructor/admin has posted it; auditors can review invoice and GL evidence.',
                glImpact: 'Posted invoice now affects GL, Trial Balance, Accounts Receivable, revenue, and AR reports.',
                nextSafeAction: 'Verify the GL lines and AR balance. Use reversal only if the posted invoice needs correction.'
            });
            toast.success(
                `AR Invoice ${inv.invoice_number} posted. It now affects GL and Trial Balance; instructors can find it in Dashboard > Recent Student Accounting Activity and Review > AR Invoices.`
            );
            fetchInvoices();
            if (onRefresh) onRefresh();
        } catch (error) {
            const detail = error.response?.data?.detail;
            toast.error(typeof detail === 'string' ? detail : 'Failed to post invoice');
        }
    };
    
    const openReversalDialog = (inv) => {
        setReversalTarget(inv);
        setReversalReason('');
        setShowReversalDialog(true);
    };

    const handleReverse = async () => {
        if (!reversalReason.trim()) {
            toast.error('Please provide a reason for reversal');
            return;
        }
        setReversing(true);
        try {
            const token = localStorage.getItem('token');
            const params = new URLSearchParams();
            if (viewingStudentId) params.append('student_id', viewingStudentId);
            const response = await axios.put(
                `${API}/ar/invoices/${reversalTarget.id}/reverse${params.toString() ? `?${params.toString()}` : ''}`,
                { reason: reversalReason },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setLastResult({
                invoiceNumber: reversalTarget.invoice_number,
                status: 'Reversed',
                customerName: reversalTarget.customer_name,
                date: reversalTarget.invoice_date,
                amount: reversalTarget.net_amount || reversalTarget.total_amount,
                reference: response.data.reversal_ref || reversalTarget.reversal_ref || 'Reversal entry',
                location: 'Record > AR Invoice, General Ledger, and reversal evidence',
                reviewState: 'Instructor/admin completed the reversal; auditors can review the reason and reference.',
                glImpact: 'Reversal creates offsetting GL entries and removes the posted invoice effect from AR balances and reports.',
                nextSafeAction: 'Review the reversal reference, then create a corrected invoice only if the customer sale still needs posting.'
            });
            toast.success(
                `AR Invoice ${reversalTarget.invoice_number} reversed. Reference: ${response.data.reversal_ref}. Review it in Record > AR Invoice and General Ledger.`
            );
            setShowReversalDialog(false);
            setReversalTarget(null);
            setReversalReason('');
            fetchInvoices();
            if (onRefresh) onRefresh();
        } catch (error) {
            const detail = error.response?.data?.detail;
            toast.error(typeof detail === 'string' ? detail : 'Failed to reverse invoice');
        } finally {
            setReversing(false);
        }
    };
    
    if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-[#2F8FCB] border-t-transparent rounded-full animate-spin" /></div>;

    const workflowScope = viewingStudentName
        ? `Reviewing ${viewingStudentName}`
        : isReadOnly
            ? 'Read-only review'
            : 'Your workspace';

    const getDisplayStatus = (invoice) => {
        if (invoice.review_status === 'returned') return 'returned';
        if (invoice.status === 'submitted') return 'submitted';
        return invoice.status || 'draft';
    };

    const getStatusBadge = (invoice) => {
        const status = getDisplayStatus(invoice);
        const styles = {
            draft: 'bg-amber-100 text-amber-700',
            submitted: 'bg-blue-100 text-blue-700',
            returned: 'bg-orange-100 text-orange-700',
            posted: 'bg-emerald-100 text-emerald-700',
            cancelled: 'bg-red-100 text-red-700',
            reversed: 'bg-purple-100 text-purple-700'
        };
        const labels = {
            draft: 'Draft',
            submitted: 'Submitted for Review',
            returned: 'Returned for Revision',
            posted: 'Posted',
            cancelled: 'Cancelled',
            reversed: 'Reversed'
        };
        return <span className={`px-2 py-0.5 rounded text-xs font-medium ${styles[status] || styles.draft}`}>{labels[status] || status}</span>;
    };

    const getInvoiceDetailTrail = (invoice) => {
        const status = getDisplayStatus(invoice);
        const invoiceNumber = invoice?.invoice_number || 'this invoice';
        const customerName = invoice?.customer_name || 'the selected customer';

        if (status === 'submitted') {
            return {
                whereToFind: `${invoiceNumber} is in Record > AR Invoice and in Review > AR/AP Review Queue for ${customerName}.`,
                reviewer: 'Instructor/admin reviews next. They can post it or return it with feedback.',
                glImpact: 'Submitted only. It does not affect GL, Trial Balance, AR balances, or reports until posted.',
                nextSafeAction: 'Wait for instructor/admin review, or revise only if it is returned.'
            };
        }

        if (status === 'returned') {
            return {
                whereToFind: `${invoiceNumber} stays in Record > AR Invoice as Returned for Revision.`,
                reviewer: 'Student revises next using the return feedback, then resubmits for review.',
                glImpact: 'Returned invoices remain draft evidence. They do not affect GL, Trial Balance, AR balances, or reports.',
                nextSafeAction: 'Review the feedback, correct the invoice, and submit it again when ready.'
            };
        }

        if (status === 'posted') {
            return {
                whereToFind: `${invoiceNumber} appears in AR Invoices, General Ledger, Trial Balance, AR Summary, and customer balance reports by invoice date.`,
                reviewer: 'No approval is waiting. Instructor/admin and auditor roles can review the posted source evidence.',
                glImpact: 'Posted invoice affects revenue, receivable balances, GL, Trial Balance, and AR reports.',
                nextSafeAction: 'Verify the GL lines and AR balance. Use reversal only if the posted invoice needs correction.'
            };
        }

        if (status === 'reversed') {
            return {
                whereToFind: `${invoiceNumber} remains in AR Invoices as reversed${invoice?.reversal_ref ? ` with reversal reference ${invoice.reversal_ref}` : ''}. Check General Ledger and Audit Trail for the offsetting entry.`,
                reviewer: 'No approval is waiting. Auditor/instructor can review reversal reason and reference as evidence.',
                glImpact: 'Reversal entries offset the original posted invoice effect in GL, Trial Balance, AR balances, and reports.',
                nextSafeAction: 'Review the reversal evidence, then create a corrected invoice only if the sale still needs posting.'
            };
        }

        if (status === 'cancelled') {
            return {
                whereToFind: `${invoiceNumber} stays in AR Invoices as cancelled source evidence.`,
                reviewer: 'No approval is waiting for a cancelled invoice.',
                glImpact: 'Cancelled invoice evidence does not affect GL, Trial Balance, AR balances, or reports.',
                nextSafeAction: 'Create a new invoice only if the customer sale still needs to be recorded.'
            };
        }

        return {
            whereToFind: `${invoiceNumber} is saved in Record > AR Invoice as a draft for ${customerName}.`,
            reviewer: draftReviewState,
            glImpact: 'Draft only. It does not affect GL, Trial Balance, AR balances, or reports until posted.',
            nextSafeAction: draftNextAction
        };
    };

    const renderInvoiceDetailTrail = (invoice) => {
        const trail = getInvoiceDetailTrail(invoice);
        return (
            <div className="rounded-lg border border-blue-100 bg-blue-50 p-3 text-blue-900" data-testid="ar-invoice-detail-trail">
                <p className="font-semibold">AR Invoice evidence trail</p>
                <div className="mt-3 grid gap-3 md:grid-cols-2">
                    <div>
                        <p className="text-xs font-semibold uppercase text-blue-700">Where to find it</p>
                        <p className="mt-1 text-slate-900">{trail.whereToFind}</p>
                    </div>
                    <div>
                        <p className="text-xs font-semibold uppercase text-blue-700">Who reviews next</p>
                        <p className="mt-1 text-slate-900">{trail.reviewer}</p>
                    </div>
                    <div>
                        <p className="text-xs font-semibold uppercase text-blue-700">GL / Trial Balance</p>
                        <p className="mt-1 text-slate-900">{trail.glImpact}</p>
                    </div>
                    <div>
                        <p className="text-xs font-semibold uppercase text-blue-700">Next safe action</p>
                        <p className="mt-1 text-slate-900">{trail.nextSafeAction}</p>
                    </div>
                </div>
                <p className="mt-3 rounded-md border border-blue-200 bg-white/70 px-3 py-2 text-xs text-blue-800">
                    Opening this source detail is read-only. It does not save, post, reverse, delete, or change GL records.
                </p>
            </div>
        );
    };

    const invoiceSubtotal = parseFloat(form.total_amount) || 0;
    const invoiceTax = parseFloat(form.tax_amount) || 0;
    const invoiceTotal = invoiceSubtotal + invoiceTax;
    
    return (
        <div className="space-y-4" data-testid="ar-invoices-tab">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-bold text-slate-900">AR Invoices</h2>
                    <p className="text-sm text-slate-500">
                        {viewingStudentName ? `Review customer invoices for ${viewingStudentName}` : 'Manage customer invoices'}
                    </p>
                </div>
                {!isReadOnly && perms.can_create !== false && (
                    <Button onClick={() => setShowForm(true)} className="bg-[#2F8FCB]" data-testid="new-invoice-btn">
                        <Plus className="w-4 h-4 mr-2" /> New Invoice
                    </Button>
                )}
            </div>
            {isReadOnly && (
                <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900" data-testid="ar-invoices-readonly-scope">
                    Student review mode is read-only. Use this list to verify invoice source evidence. Approve & Post or Return actions are in Review &gt; AR/AP Review Queue.
                </div>
            )}

            <ReportWorkflowTrail
                title="AR Invoices trail"
                testId="ar-invoices-workflow-trail"
                scope={workflowScope}
                items={[
                    {
                        label: 'Source work',
                        value: 'AR invoices record customer billings, revenue, and receivable support before collection.'
                    },
                    {
                        label: 'Where it goes',
                        value: 'Saved invoices stay in Record > AR Invoice and appear in instructor activity for review.'
                    },
                    {
                        label: 'Approval',
                        value: canPostInCurrentScope
                            ? 'Instructors/admins post or reverse after checking customer and sales support.'
                            : 'Students submit AR invoices for instructor/admin review. Submitted work appears in Review > AR/AP Review Queue.'
                    },
                    {
                        label: 'GL and TB',
                        value: 'Only posted invoices and reversal entries affect GL, Trial Balance, AR balances, and reports.'
                    },
                    {
                        label: 'Next safe action',
                        value: canPostInCurrentScope
                            ? 'Review customer, amount, date, and support before posting. Use reversal for posted corrections.'
                            : 'Save the invoice, then submit it for review. It affects GL/Trial Balance only after instructor posting.'
                    }
                ]}
            />

            {lastResult && (
                <div className="rounded-lg border border-blue-100 bg-blue-50 p-4" data-testid="ar-invoice-result-trail">
                    <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                        <div>
                            <p className="text-xs font-semibold uppercase tracking-wide text-blue-600">Last AR invoice action</p>
                            <h3 className="mt-1 text-base font-bold text-[#0F2A44]">
                                {lastResult.invoiceNumber} - {lastResult.status}
                            </h3>
                            <p className="text-sm text-blue-900 mt-1">
                                {lastResult.customerName} - {lastResult.date} - {fmt(lastResult.amount || 0)}
                            </p>
                            {lastResult.reference && (
                                <p className="text-xs font-mono text-blue-700 mt-1">Reference: {lastResult.reference}</p>
                            )}
                        </div>
                        <span className={`w-fit rounded-full px-3 py-1 text-xs font-semibold ${
                            lastResult.status === 'Posted'
                                ? 'bg-emerald-100 text-emerald-700'
                                : lastResult.status === 'Reversed'
                                    ? 'bg-purple-100 text-purple-700'
                                    : 'bg-amber-100 text-amber-700'
                        }`}>
                            {lastResult.status}
                        </span>
                    </div>
                    <div className="mt-3 grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
                        <div className="rounded-lg bg-white border border-blue-100 p-3">
                            <p className="text-xs font-semibold uppercase text-slate-500">Where it is now</p>
                            <p className="mt-1 text-slate-900">{lastResult.location}</p>
                        </div>
                        <div className="rounded-lg bg-white border border-blue-100 p-3">
                            <p className="text-xs font-semibold uppercase text-slate-500">Review state</p>
                            <p className="mt-1 text-slate-900">{lastResult.reviewState}</p>
                        </div>
                        <div className="rounded-lg bg-white border border-blue-100 p-3">
                            <p className="text-xs font-semibold uppercase text-slate-500">GL / Trial Balance</p>
                            <p className="mt-1 text-slate-900">{lastResult.glImpact}</p>
                        </div>
                        <div className="rounded-lg bg-white border border-blue-100 p-3">
                            <p className="text-xs font-semibold uppercase text-slate-500">Next safe action</p>
                            <p className="mt-1 text-slate-900">{lastResult.nextSafeAction}</p>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Quick Add Form */}
            {showForm && (
                <div className="bg-white border border-slate-200 rounded-lg p-4">
                    <h3 className="font-semibold mb-4">New Invoice</h3>
                    <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                        <div>
                            <Label>Customer *</Label>
                            <Select value={form.customer_id} onValueChange={(v) => setForm(p => ({ ...p, customer_id: v }))}>
                                <SelectTrigger className="mt-1" data-testid="invoice-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
                                <SelectContent>
                                    {customers.map((customer) => {
                                        const value = getCustomerOptionValue(customer);
                                        if (!value) return null;
                                        const label = customer.customer_code
                                            ? `${customer.customer_name} (${customer.customer_code})`
                                            : customer.customer_name;
                                        return <SelectItem key={customer.id || customer.customer_id || value} value={value}>{label}</SelectItem>;
                                    })}
                                    {customers.length === 0 && (
                                        <SelectItem value="__no_customers" disabled>No active customers</SelectItem>
                                    )}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Invoice Date</Label>
                            <Input type="date" value={form.invoice_date} onChange={(e) => setForm(p => ({ ...p, invoice_date: e.target.value }))} className="mt-1" />
                        </div>
                        <div>
                            <Label>Particulars *</Label>
                            <Input value={form.particulars} onChange={(e) => setForm(p => ({ ...p, particulars: e.target.value }))} placeholder="Description" className="mt-1" data-testid="invoice-particulars-input" />
                        </div>
                        <div>
                            <Label>Subtotal *</Label>
                            <Input type="number" value={form.total_amount} onChange={(e) => setForm(p => ({ ...p, total_amount: parseFloat(e.target.value) || 0 }))} className="mt-1" data-testid="invoice-amount-input" />
                        </div>
                        <div>
                            <Label>VAT / Tax</Label>
                            <Input type="number" value={form.tax_amount} onChange={(e) => setForm(p => ({ ...p, tax_amount: parseFloat(e.target.value) || 0 }))} className="mt-1" data-testid="invoice-tax-input" />
                        </div>
                        <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                            <p className="text-xs font-semibold uppercase text-slate-500">Invoice Total</p>
                            <p className="mt-1 font-mono text-sm font-semibold text-slate-900" data-testid="invoice-total-preview">{fmt(invoiceTotal)}</p>
                        </div>
                    </div>
                    <div className="flex gap-2 mt-4">
                        <Button onClick={handleCreate} className="bg-emerald-600" data-testid="save-invoice-btn">Save Invoice</Button>
                        <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
                    </div>
                </div>
            )}
            
            {/* Invoice List */}
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                <table className="w-full min-w-[860px] text-sm">
                    <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                        <tr>
                            <th className="text-left px-4 py-3">Invoice #</th>
                            <th className="text-left px-4 py-3">Customer</th>
                            <th className="text-left px-4 py-3">Date</th>
                            <th className="text-right px-4 py-3">Invoice Total</th>
                            <th className="text-right px-4 py-3">Balance</th>
                            <th className="text-center px-4 py-3">Status</th>
                            <th className="text-center px-4 py-3">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {invoices.length === 0 ? (
                            <tr><td colSpan="7" className="px-4 py-8 text-center text-slate-400">No invoices yet</td></tr>
                        ) : (
                            invoices.map(inv => (
                                <tr key={inv.id} className="hover:bg-slate-50">
                                    <td className="px-4 py-3 font-mono">{inv.invoice_number}</td>
                                    <td className="px-4 py-3">
                                        <p className="font-medium">{inv.customer_name}</p>
                                        {inv.customer_tin && (
                                            <p className="text-xs text-slate-500">TIN {inv.customer_tin}</p>
                                        )}
                                        {getAtcSummary(inv, 'customer') && (
                                            <p className="text-xs text-slate-500">ATC {getAtcSummary(inv, 'customer')}</p>
                                        )}
                                    </td>
                                    <td className="px-4 py-3">{inv.invoice_date}</td>
                                    <td className="px-4 py-3 text-right font-mono">{fmt(inv.net_amount)}</td>
                                    <td className="px-4 py-3 text-right font-mono text-amber-600">{fmt(inv.balance)}</td>
                                    <td className="px-4 py-3 text-center">
                                        {getStatusBadge(inv)}
                                        {inv.review_status === 'returned' && (
                                            <div className="mx-auto mt-2 max-w-[240px] rounded-md border border-orange-200 bg-orange-50 px-2 py-1 text-left text-xs text-orange-800" data-testid={`returned-ar-invoice-row-note-${inv.invoice_number}`}>
                                                <p className="font-semibold">Returned: revise this invoice, then resubmit.</p>
                                                <p className="mt-0.5 line-clamp-2">Feedback: {inv.return_reason || 'Open the invoice details to read instructor feedback.'}</p>
                                                <p className="mt-0.5">No GL, Trial Balance, or AR report impact until posted.</p>
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                        <TooltipProvider>
                                        <div className="flex items-center justify-center gap-1">
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => setSelectedInvoice(inv)}
                                                        data-testid={`view-invoice-${inv.invoice_number}`}
                                                        aria-label={`View invoice ${inv.invoice_number} source details`}
                                                        title={`View invoice ${inv.invoice_number} source details`}
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                    </Button>
                                                </TooltipTrigger>
                                                <TooltipContent><p>View invoice source details</p></TooltipContent>
                                            </Tooltip>
                                            {inv.status === 'draft' && !isReadOnly && (
                                                perms.can_post ? (
                                                    <Button
                                                        size="sm"
                                                        onClick={() => handlePost(inv)}
                                                        className="bg-emerald-600 text-xs"
                                                        data-testid={`post-invoice-${inv.invoice_number}`}
                                                        aria-label={`Post invoice ${inv.invoice_number}`}
                                                        title={`Post invoice ${inv.invoice_number}`}
                                                    >
                                                        Post
                                                    </Button>
                                                ) : (
                                                    <Button
                                                        size="sm"
                                                        onClick={() => handleSubmitForReview(inv)}
                                                        className="bg-blue-600 text-xs hover:bg-blue-700"
                                                        data-testid={`submit-invoice-${inv.invoice_number}`}
                                                        aria-label={`Submit invoice ${inv.invoice_number} for review`}
                                                        title={`Submit invoice ${inv.invoice_number} for review`}
                                                    >
                                                        <Send className="w-3 h-3 mr-1" /> Submit
                                                    </Button>
                                                )
                                            )}
                                            {inv.status === 'submitted' && !isReadOnly && (
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="text-blue-500 cursor-not-allowed"
                                                            disabled
                                                            aria-label={`Invoice ${inv.invoice_number} is submitted to AR/AP Review Queue`}
                                                            title={`Invoice ${inv.invoice_number} is submitted to AR/AP Review Queue`}
                                                        >
                                                            <Lock className="w-4 h-4" />
                                                        </Button>
                                                    </TooltipTrigger>
                                                    <TooltipContent><p>Submitted to Review &gt; AR/AP Review Queue</p></TooltipContent>
                                                </Tooltip>
                                            )}
                                            {inv.status === 'posted' && (
                                                <>
                                                    {canReverseInCurrentScope ? (
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    className="text-purple-600"
                                                                    onClick={() => openReversalDialog(inv)}
                                                                    data-testid={`reverse-invoice-${inv.invoice_number}`}
                                                                    aria-label={`Reverse invoice ${inv.invoice_number}`}
                                                                    title={`Reverse invoice ${inv.invoice_number}`}
                                                                >
                                                                    <Undo2 className="w-4 h-4" />
                                                                </Button>
                                                            </TooltipTrigger>
                                                            <TooltipContent><p>Reverse this posted invoice with a required reason</p></TooltipContent>
                                                        </Tooltip>
                                                    ) : (
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    className="text-slate-300 cursor-not-allowed"
                                                                    disabled
                                                                    aria-label={`Reverse invoice ${inv.invoice_number} unavailable`}
                                                                    title={`Reverse invoice ${inv.invoice_number} unavailable`}
                                                                >
                                                                    <Lock className="w-4 h-4" />
                                                                </Button>
                                                            </TooltipTrigger>
                                                            <TooltipContent><p>Only instructors can reverse invoices</p></TooltipContent>
                                                        </Tooltip>
                                                    )}
                                                </>
                                            )}
                                            {isReadOnly && !(inv.status === 'posted' && canReverseInCurrentScope) && (
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="text-slate-300 cursor-not-allowed"
                                                            disabled
                                                            aria-label={`Invoice ${inv.invoice_number} is read-only in this review scope`}
                                                            title={`Invoice ${inv.invoice_number} is read-only in this review scope`}
                                                        >
                                                            <Lock className="w-4 h-4" />
                                                        </Button>
                                                    </TooltipTrigger>
                                                    <TooltipContent><p>Read-only student review mode</p></TooltipContent>
                                                </Tooltip>
                                            )}
                                            {inv.status === 'reversed' && inv.reversal_ref && (
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <span className="text-xs text-purple-600 font-mono cursor-help">{inv.reversal_ref}</span>
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                        <p>Reversed by: {inv.reversed_by_name || 'N/A'}</p>
                                                        <p>Reason: {inv.reversal_reason || 'N/A'}</p>
                                                        {inv.reversed_at && <p>At: {new Date(inv.reversed_at).toLocaleString()}</p>}
                                                    </TooltipContent>
                                                </Tooltip>
                                            )}
                                        </div>
                                        </TooltipProvider>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
                </div>
            </div>

            {/* View Invoice Dialog */}
            <Dialog open={!!selectedInvoice} onOpenChange={() => setSelectedInvoice(null)}>
                <DialogContent className="max-w-xl">
                    <DialogHeader>
                        <DialogTitle>AR Invoice Source Details</DialogTitle>
                        <DialogDescription>
                            Review where this invoice is saved, who handles it next, and when it affects GL, Trial Balance, AR balances, and reports.
                        </DialogDescription>
                    </DialogHeader>
                    {selectedInvoice && (
                        <div className="space-y-4 text-sm">
                            <div className="grid grid-cols-2 gap-4">
                                <div><span className="text-slate-500">Invoice #:</span> <strong>{selectedInvoice.invoice_number}</strong></div>
                                <div className="flex items-center gap-2"><span className="text-slate-500">Status:</span> {getStatusBadge(selectedInvoice)}</div>
                                <div><span className="text-slate-500">Customer:</span> {selectedInvoice.customer_name}</div>
                                <div><span className="text-slate-500">Date:</span> {selectedInvoice.invoice_date}</div>
                                <div><span className="text-slate-500">Subtotal:</span> <strong>{fmt(selectedInvoice.subtotal ?? selectedInvoice.total_amount ?? 0)}</strong></div>
                                <div><span className="text-slate-500">VAT / Tax:</span> <strong>{fmt(selectedInvoice.tax_amount || 0)}</strong></div>
                                <div><span className="text-slate-500">Invoice Total:</span> <strong className="text-emerald-600">{fmt(selectedInvoice.net_amount || selectedInvoice.total_amount || 0)}</strong></div>
                                <div><span className="text-slate-500">Balance:</span> <strong className="text-amber-600">{fmt(selectedInvoice.balance || 0)}</strong></div>
                            </div>
                            {(selectedInvoice.customer_tin || getAtcSummary(selectedInvoice, 'customer')) && (
                                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                    <p className="font-semibold text-slate-700 mb-2">Customer evidence</p>
                                    <div className="grid grid-cols-2 gap-2">
                                        <div><span className="text-slate-500">TIN:</span> {selectedInvoice.customer_tin || '-'}</div>
                                        <div><span className="text-slate-500">ATC:</span> {getAtcSummary(selectedInvoice, 'customer') || '-'}</div>
                                    </div>
                                </div>
                            )}
                            {selectedInvoice.particulars && (
                                <div>
                                    <span className="text-slate-500">Particulars:</span>
                                    <p>{selectedInvoice.particulars}</p>
                                </div>
                            )}
                            {selectedInvoice.review_status === 'returned' && (
                                <div className="rounded-lg border border-orange-200 bg-orange-50 p-3 text-orange-900">
                                    <p className="font-semibold">Returned for revision</p>
                                    <p className="mt-1">{selectedInvoice.return_reason || 'Please revise this invoice and resubmit for review.'}</p>
                                    {selectedInvoice.returned_at && (
                                        <p className="mt-1 text-xs text-orange-700">Returned at {new Date(selectedInvoice.returned_at).toLocaleString()}</p>
                                    )}
                                </div>
                            )}
                            {renderInvoiceDetailTrail(selectedInvoice)}
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setSelectedInvoice(null)}>Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Reversal Confirmation Dialog */}
            <Dialog open={showReversalDialog} onOpenChange={setShowReversalDialog}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-purple-700">Reverse AR Invoice</DialogTitle>
                        <DialogDescription>
                            This creates reversing GL entries, marks the invoice as reversed, and preserves the original posted invoice for audit evidence.
                        </DialogDescription>
                    </DialogHeader>
                    {reversalTarget && (
                        <div className="bg-slate-50 rounded-lg p-3 mb-4">
                            <p className="text-sm"><strong>Invoice:</strong> {reversalTarget.invoice_number}</p>
                            <p className="text-sm"><strong>Customer:</strong> {reversalTarget.customer_name}</p>
                            {reversalTarget.customer_tin && (
                                <p className="text-sm"><strong>TIN:</strong> {reversalTarget.customer_tin}</p>
                            )}
                            {getAtcSummary(reversalTarget, 'customer') && (
                                <p className="text-sm"><strong>ATC:</strong> {getAtcSummary(reversalTarget, 'customer')}</p>
                            )}
                            <p className="text-sm"><strong>Amount:</strong> {fmt(reversalTarget.net_amount)}</p>
                        </div>
                    )}
                    <div className="space-y-2">
                        <Label htmlFor="ar-inv-reversal-reason">Reason for Reversal *</Label>
                        <Textarea
                            id="ar-inv-reversal-reason"
                            placeholder="e.g., Incorrect customer, duplicate entry, etc."
                            value={reversalReason}
                            onChange={(e) => setReversalReason(e.target.value)}
                            rows={3}
                            data-testid="ar-inv-reversal-reason-input"
                        />
                    </div>
                    <DialogFooter className="mt-4">
                        <Button variant="outline" onClick={() => setShowReversalDialog(false)}>Cancel</Button>
                        <Button
                            onClick={handleReverse}
                            disabled={reversing || !reversalReason.trim()}
                            className="bg-purple-600 hover:bg-purple-700"
                            data-testid="ar-inv-confirm-reversal-btn"
                        >
                            {reversing ? 'Reversing...' : 'Confirm Reversal'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

// ================= AR COLLECTIONS TAB (Simplified) =================


export default ARInvoicesTab;
